import React, { useEffect, useState } from 'react'

import Button from '@react/react-spectrum/Button'
import Dialog from '@react/react-spectrum/Dialog'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import Radio from '@react/react-spectrum/Radio'
import RadioGroup from '@react/react-spectrum/RadioGroup'
import Select from '@react/react-spectrum/Select'
import Textfield from '@react/react-spectrum/Textfield'
import { KeyValueTable } from 'components/Table/KeyValueTable'

import { Tile } from './tiles'

const PROVIDERS = {
  SINCH: 'sinch',
  DISABLED: 'disabled',
}

const MODES = {
  SELECT: 'select',
  CUSTOM: 'custom',
}

export function ChatBotTile({ chatBot, chatBotOptions, disabled, onChange }) {
  const relevantChatBotOptions = chatBotOptions[chatBot?.provider]

  const label = (chatBot?.id && relevantChatBotOptions?.find((opt) => opt.value === chatBot?.id)?.label) || chatBot?.id
  const isSet = !!label

  return (
    <Tile name='Chat Bot' text={label} active={isSet}>
      <ModalTrigger>
        <Button disabled={disabled}>Configure</Button>
        <ChatBotDialog chatBot={chatBot} chatBotOptions={chatBotOptions} disabled={disabled} onChange={onChange} />
      </ModalTrigger>
    </Tile>
  )
}

function ChatBotDialog({ chatBot, chatBotOptions, disabled, onChange, ...otherProps }) {
  const [config, setConfig] = useState(chatBot)
  const onSave = () => onChange(config)

  return (
    <Dialog
      title='Chat Bot configuration'
      confirmLabel='Save'
      onConfirm={onSave}
      cancelLabel='Discard'
      {...otherProps}
      role='dialog'
    >
      <ChatBotForm chatBot={config} chatBotOptions={chatBotOptions} canEdit={!disabled} updateChatBot={setConfig} />
    </Dialog>
  )
}

export function ChatBotForm({ chatBot, chatBotOptions, canEdit, updateChatBot }) {
  const [chatBotId, setChatBotId] = useState(chatBot?.id || '')
  const [provider, setProvider] = useState(chatBot?.provider || PROVIDERS.DISABLED)

  const relevantChatBotOptions = chatBotOptions[provider] || []
  const currentOption = relevantChatBotOptions.find((opt) => opt.value === chatBot?.id)
  const isCustomBotId = !!chatBotId && !currentOption

  const [mode, setMode] = useState(chatBot?.id ? (isCustomBotId ? MODES.CUSTOM : MODES.SELECT) : MODES.SELECT)

  const requiredParams = mode === MODES.SELECT ? currentOption?.requiredParams || [] : null
  const optionalParams = mode === MODES.SELECT ? currentOption?.optionalParams || [] : null

  const onProviderChange = (newProvider) => {
    if (newProvider === PROVIDERS.DISABLED) {
      updateChatBot(null)
      setProvider(newProvider)
      return
    }

    const providerOptions = chatBotOptions[newProvider]
    if (providerOptions.length > 0) {
      const defaultOption = providerOptions.find((o) => o.default === true) || providerOptions[0]
      updateChatBot({
        provider: newProvider,
        id: defaultOption.value,
        params: Object.entries(defaultOption.params).map(([key, value]) => ({ key, value })),
      })
      setProvider(newProvider)
    }
  }

  const selectInstance = (v) => {
    const selectedOption = relevantChatBotOptions.find((o) => o.value === v)
    if (!selectedOption) return

    const { value, params } = selectedOption
    setChatBotId(value)
    updateChatBot({ ...chatBot, id: value, params: Object.entries(params).map(([key, value]) => ({ key, value })) })
  }

  const updateParams = (params) => {
    updateChatBot({ ...chatBot, params })
  }

  useEffect(() => {
    if (!!chatBotId) {
      setChatBotId(chatBot?.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatBot?.id])

  useEffect(() => {
    updateChatBot({
      ...chatBot,
      id: chatBotId,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatBotId])

  return (
    <div className='chatBotForm'>
      <FieldLabel label='Chat Bot Provider'>
        <RadioGroup selectedValue={provider} onChange={onProviderChange} disabled={!canEdit}>
          <Radio label='Sinch (recommended)' value={PROVIDERS.SINCH} disabled={!canEdit} />
          <Radio label='Disabled' value={PROVIDERS.DISABLED} disabled={!canEdit} />
        </RadioGroup>
      </FieldLabel>

      {provider !== PROVIDERS.DISABLED && (
        <>
          <FieldLabel label='Configuration method'>
            <RadioGroup selectedValue={mode} onChange={setMode} disabled={!canEdit}>
              <Radio label='Pre-defined' value={MODES.SELECT} disabled={!canEdit || !relevantChatBotOptions.length} />
              <Radio label='Custom' value={MODES.CUSTOM} disabled={!canEdit} />
            </RadioGroup>
          </FieldLabel>

          {mode === MODES.SELECT && (
            <FieldLabel label='ChatBot Version'>
              <Select
                value={chatBot?.id}
                options={relevantChatBotOptions}
                onSelect={selectInstance}
                disabled={!canEdit}
              />
            </FieldLabel>
          )}
          {mode === MODES.CUSTOM && (
            <FieldLabel label='ChatBot ID'>
              <Textfield value={chatBotId} placeholder='ChatBot ID' onChange={setChatBotId} disabled={!canEdit} />
            </FieldLabel>
          )}
          <div className='chatBotForm__table'>
            <KeyValueTable
              entries={chatBot?.params}
              allowedKeys={optionalParams}
              requiredKeys={requiredParams}
              disabled={!canEdit}
              onChange={updateParams}
            />
          </div>
        </>
      )}
    </div>
  )
}
